import React from 'react'
import { motion, type Variants } from 'framer-motion'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { UserCircle } from '@phosphor-icons/react'
import { Envelope, ChartBar, Users, Sparkle } from '@phosphor-icons/react'
import { BrandWordmark } from '@/components/ui/Logomark'

interface AuthLayoutProps {
  /** Optional — omit it and the matched child route renders via <Outlet />. */
  children?: React.ReactNode
}

const features = [
  {
    icon: Sparkle,
    title: 'AI-Powered Outreach',
    description: 'Generate hyper-personalized emails that feel hand-written',
  },
  {
    icon: Users,
    title: 'Smart Contact Enrichment',
    description: 'Pull company and contact data in automatically',
  },
  {
    icon: Envelope,
    title: 'Automated Campaigns',
    description: 'Multi-step sequences with intelligent follow-ups',
  },
  {
    icon: ChartBar,
    title: 'Real-time Analytics',
    description: 'Track opens, clicks, replies, and conversions',
  },
]

// Footer strip copy — must name only capabilities that ship in the product
// (see DashboardLayout navGroups). No counts, no customer claims.
const capabilities = [
  'Workflow builder',
  'Contact enrichment',
  'Campaigns',
  'Analytics',
]


const linearFontStyle: React.CSSProperties = {
  fontFamily: "'Inter', -apple-system, system-ui, 'Segoe UI', Roboto, sans-serif",
  fontFeatureSettings: '"cv01", "ss03"',
}

// SVG fractal-noise grain — data URI, used as background-image on overlay layers
const grainDataUri =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.55 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>"

const meshLeftBackground =
  'radial-gradient(at 18% 22%, rgba(94,106,210,0.10) 0px, transparent 55%),' +
  'radial-gradient(at 82% 72%, rgba(113,112,255,0.08) 0px, transparent 55%),' +
  'radial-gradient(at 60% 4%, rgba(94,106,210,0.05) 0px, transparent 45%)'

const meshRightBackground =
  'radial-gradient(at 78% 18%, rgba(113,112,255,0.06) 0px, transparent 50%),' +
  'radial-gradient(at 12% 88%, rgba(94,106,210,0.05) 0px, transparent 55%)'

const easeOutExpo = [0.16, 1, 0.3, 1] as const

const stageVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.05 },
  },
}

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: easeOutExpo } },
}

const featureGridVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.07, delayChildren: 0.1 },
  },
}

const featureCardVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOutExpo } },
}

/** The admin door shows on the sign-in page only, not on register/reset/OTP. */
export function showsAdminEntry(pathname: string): boolean {
  return pathname === '/login'
}

const AuthLayout: React.FC<AuthLayoutProps> = ({ children }) => {
  const { pathname } = useLocation()
  return (
    <div
      className="min-h-[100dvh] flex bg-[#fafafa] dark:bg-[#08090a]"
      style={linearFontStyle}
    >
      {/* Left Panel — Marketing Canvas */}
      <motion.div
        className="hidden lg:flex lg:w-[45%] flex-col justify-between p-12 pl-16 relative overflow-hidden bg-[#fafafa] border-r border-black/[0.06] dark:bg-[#08090a] dark:border-white/[0.05]"
        variants={stageVariants}
        initial="hidden"
        animate="show"
      >
        {/* Atmosphere layer 1 — gradient mesh */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{ backgroundImage: meshLeftBackground }}
        />

        {/* Atmosphere layer 2 — subtle dot grid (light) */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.5] dark:opacity-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle, rgba(15,16,17,0.06) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />
        {/* Atmosphere layer 2 — subtle dot grid (dark) */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-0 dark:opacity-[0.25] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.035) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />

        {/* Atmosphere layer 3 — film grain */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none opacity-[0.04] dark:opacity-[0.08]"
          style={{
            backgroundImage: `url("${grainDataUri}")`,
            backgroundSize: '220px 220px',
            mixBlendMode: 'overlay',
          }}
        />

        {/* Oversized decorative ampersand — bleeds off bottom-right edge */}
        <motion.div
          aria-hidden="true"
          className="absolute pointer-events-none select-none text-[#7170ff]"
          style={{
            fontFamily: "'Instrument Serif', Georgia, serif",
            fontStyle: 'italic',
            fontSize: '560px',
            lineHeight: 1,
            opacity: 0.06,
            right: '-120px',
            bottom: '-180px',
          }}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 0.06, scale: 1 }}
          transition={{ duration: 1.4, ease: easeOutExpo, delay: 0.2 }}
        >
          &amp;
        </motion.div>

        {/* Diagonal hairline accent — top-left to mid panel */}
        <motion.div
          aria-hidden="true"
          className="absolute pointer-events-none"
          style={{
            top: '14%',
            left: '-10%',
            width: '70%',
            height: '1px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(113,112,255,0.18) 50%, transparent 100%)',
            transformOrigin: 'left center',
          }}
          initial={{ opacity: 0, scaleX: 0, rotate: -18 }}
          animate={{ opacity: 1, scaleX: 1, rotate: -18 }}
          transition={{ duration: 1.2, ease: easeOutExpo, delay: 0.4 }}
        />

        {/* Logo */}
        <motion.div className="relative" variants={itemVariants}>
          <BrandWordmark height={30} />
        </motion.div>

        {/* Hero + Features */}
        <div className="relative space-y-14">
          <div>
            <motion.h1
              className="text-[#0f1011] dark:text-[#f7f8f8] relative"
              style={{
                fontFamily: "'Instrument Serif', 'Iowan Old Style', 'Apple Garamond', Georgia, serif",
                fontSize: 'clamp(48px, 5.2vw, 60px)',
                lineHeight: 1.0,
                letterSpacing: '-1.4px',
                fontWeight: 400,
                marginLeft: '-2px',
              }}
              variants={itemVariants}
            >
              AI-powered email
              <br />
              marketing that{' '}
              <span
                className="text-[#5e6ad2] dark:text-[#7170ff]"
                style={{ fontStyle: 'italic' }}
              >
                converts.
              </span>
            </motion.h1>
            <motion.p
              className="text-[#5f6166] dark:text-[#8a8f98] mt-7 max-w-md"
              style={{
                fontSize: '15px',
                lineHeight: 1.6,
                letterSpacing: '-0.165px',
                fontWeight: 400,
              }}
              variants={itemVariants}
            >
              Research contacts, generate personalized emails, and automate campaigns — all in one platform.
            </motion.p>
          </div>

          <motion.div
            className="grid grid-cols-2 gap-4"
            variants={featureGridVariants}
          >
            {features.map((feature) => (
              <motion.div
                key={feature.title}
                className="p-5 rounded-lg bg-white border border-black/[0.06] dark:bg-white/[0.02] dark:border-white/[0.08] transition-colors duration-200"
                variants={featureCardVariants}
                whileHover={{
                  y: -2,
                  borderColor: 'rgba(113,112,255,0.25)',
                  transition: { duration: 0.2, ease: easeOutExpo },
                }}
              >
                <div className="h-9 w-9 rounded-md bg-[#5e6ad2]/[0.08] border border-[#5e6ad2]/[0.12] dark:bg-white/[0.04] dark:border-white/[0.05] flex items-center justify-center mb-4">
                  <feature.icon className="h-[18px] w-[18px] text-[#5e6ad2] dark:text-[#7170ff]" weight="regular" />
                </div>
                <p
                  className="text-[#0f1011] dark:text-[#f7f8f8]"
                  style={{ fontSize: '15px', fontWeight: 510, letterSpacing: '-0.182px' }}
                >
                  {feature.title}
                </p>
                <p
                  className="text-[#5f6166] dark:text-[#8a8f98] mt-1.5"
                  style={{ fontSize: '13px', lineHeight: 1.5, letterSpacing: '-0.13px', fontWeight: 400 }}
                >
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Footer strip — factual capability list. No metrics, counts, or testimonials. */}
        <motion.div className="relative" variants={itemVariants}>
          <div className="border-t border-black/[0.06] dark:border-white/[0.05] pt-5">
            <p
              className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[#7a7d83] dark:text-[#62666d]"
              style={{
                fontFamily: "'Berkeley Mono', ui-monospace, 'SF Mono', Menlo, monospace",
                fontSize: '10px',
                fontWeight: 400,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
              }}
            >
              {capabilities.map((capability, i) => (
                <React.Fragment key={capability}>
                  {i > 0 && <span aria-hidden="true">·</span>}
                  <span>{capability}</span>
                </React.Fragment>
              ))}
            </p>
          </div>
        </motion.div>
      </motion.div>

      {/* Right Panel — Auth Form */}
      <div
        className="w-full lg:w-[55%] flex items-center justify-center px-6 py-10 bg-white dark:bg-[#0f1011] relative overflow-hidden shadow-[inset_0_0_120px_rgba(15,16,17,0.04)] dark:shadow-[inset_0_0_140px_rgba(0,0,0,0.55)]"
      >
        {/* Atmosphere — gradient mesh */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{ backgroundImage: meshRightBackground }}
        />

        {/* Atmosphere — film grain */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none opacity-[0.03] dark:opacity-[0.06]"
          style={{
            backgroundImage: `url("${grainDataUri}")`,
            backgroundSize: '220px 220px',
            mixBlendMode: 'overlay',
          }}
        />

        {/* Team entry — a discreet way into the admin panel from the sign-in page
            only. The panel has its own login and token; this is just a door. */}
        {showsAdminEntry(pathname) && (
          <Link
            to="/admin/login"
            aria-label="Team sign in"
            title="Team sign in"
            className="absolute top-6 right-6 z-10 rounded-full p-1.5 text-[#0f1011]/40 hover:text-[#0f1011] dark:text-white/40 dark:hover:text-white transition-colors"
          >
            <UserCircle size={22} weight="regular" />
          </Link>
        )}

        {/* Mobile Logo */}
        <motion.div
          className="lg:hidden absolute top-6 left-6"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: easeOutExpo }}
        >
          <BrandWordmark height={26} />
        </motion.div>

        <div className="w-full max-w-[400px] relative">
          {children ?? <Outlet />}
        </div>
      </div>
    </div>
  )
}

export default AuthLayout
